import { configureStore, AnyAction, Reducer } from '@reduxjs/toolkit';
import type { PreloadedState } from '@reduxjs/toolkit';
import { useDispatch, useSelector, TypedUseSelectorHook } from 'react-redux';
import {
  persistCombineReducers,
  persistStore,
  FLUSH,
  REHYDRATE,
  PAUSE,
  PERSIST,
  PURGE,
  REGISTER,
} from 'reduxjs-toolkit-persist';
import createWebStorage from 'reduxjs-toolkit-persist/lib/storage/createWebStorage';
import autoMergeLevel2 from 'reduxjs-toolkit-persist/lib/stateReconciler/autoMergeLevel2';
import logoutReducer, { resetStore } from './slices/logoutSlice';
import threadReducer, {
  setThreadStatus,
  threadState,
  threadStatusRegistryState,
} from './slices/threadSlice';
import relatedQuestionsReducer, {
  relatedQuestionsState,
  toggleRelatesQuestionsVisible,
} from './slices/relatedQuestions';
import threadPersistReducer, {
  messageTabPersistRegistryState,
  setMessageTabPersistRegistry,
} from './slices/threadPersistSlice';
import editorReducer, {
  editorState,
  setEditorType,
  setCodeEditorData,
  setIsWordWrapEnabled,
  setEditorIsDarkTheme,
  setEditorStreaming,
  setAutoSavingStatus,
} from './slices/editorSlice';
import abTestingReducer from './slices/abTestingSlice';
import sessionReducer, {
  resetSession,
  sessionState,
  sessionColorThemeState,
  setNotification,
  clearNotification,
  setAppUser,
  setIsDarkTheme,
  setSocketConnected,
  setOnboardingHintStep,
  setCurrentTaskId,
  showAccessRequestOnSchedulerTask,
  setIsAccessModalShown,
  setFirstUserQuery,
  setPartialAppUser,
  setActiveSuperAgentModel,
  setBasicTierWelcomeLastShown,
  setIsVirtuosoOn,
} from './slices/sessionSlice';
import activeModelReducer, {
  setActiveModel,
  activeModelState,
} from './slices/activeModelSlice';
import activeInputBoxModelReducer, {
  setActiveInputBoxModel,
  activeInputBoxModelState,
} from './slices/activeInputBoxModelSlice';
import skillCommandReducer, {
  setActiveSkillCommand,
  skillCommandState,
} from './slices/skillCommandSlice';
import paymentPageRightSidePanelReducer, {
  setActivePanelView,
  paymentPageRightSidePanelState,
} from './slices/paymentPageRightSidePanelSlice';
import manageCreditPageRightSidePanelReducer, {
  setActivePanelView as setActiveManageCreditsPanelView,
  manageCreditPageRightSidePanelState,
} from './slices/manageCreditsRightSidePanelSlice';
import choosePlanModalSliceReducer, {
  setShouldShowPlanModal as setShouldShowChoosePlanModal,
  setLastTimeModalWasShown as setLastTimeChoosePlanModalWasShown,
  setNeverShowPlanModalAgain as setNeverShowChoosePlanModalAgain,
  choosePlanModalState,
} from './slices/choosePlanModalSlice';
import productNotificationModalReducer, {
  setShouldShowNotificationModal,
  setLastTimeNotificationModalWasShown,
  productNotificationModalState,
} from './slices/productNotificationModalSlice';
import paymentControlReducer, {
  setSelectedPeriod,
  setSelectedFamily,
  setSelectedPlanByFamily,
  resetPaymentControl,
  setComplexPaymentPlan,
  paymentControlState,
} from './slices/paymentControlSlice';
import settingsReducer, {
  settingsState,
  resetSettings,
  setActiveSettingsTab,
} from './slices/settingsSlice';
import avatarReducer, {
  setAvatarQueue,
  clearAvatarQueue,
  setChatMode,
  avatarState,
} from './slices/avatarSlice';
import interfaceControlReducer, {
  setChatModeSidePanel,
  setIsSettingsPanelExpanded,
  setIsLeftPanelExpanded,
  setLeftPanelSections,
  setIsRightPanelExpanded,
  setRightPanelSections,
  setIsRightPanelFullWidth,
  setIsShowAppSwitcherTooltip,
  setIsShowRequestUsageTable,
  interfaceControlState,
} from './slices/interfaceControlSlice';
import modalsControlReducer, {
  toggleWelcomeModal,
  setWelcomeUpsellModalType,
  toggleQuickTourModal,
  modalsControlState,
} from './slices/modalsControlSlice';
import imageMaskingReducer, {
  setSelectedImageMask,
  setImageMaskingSelectionsCount,
  toggleIsOpenModalImagesList,
  setCurrentImagesList,
  setSelectedImageIndex,
  setImageMaskingRightPanelData,
  toggleVisibleBrushSizePanel,
  setImageMaskingIsLoading,
  setMessageId,
  setIsAttachedImageTooltipVisible,
  setIsAttachedImageEditingMode,
  setEditingMode,
  imageMaskingState,
  triggerCanvasReset,
} from './slices/imageMaskingSlice';
import teamsSliceReducer, {
  setIsOpenInviteUsersModal,
  setIsOpenJoinWorkspaceModal,
  setSelectedNumberOfSeatsToBuy,
  setIsFirstInviteUsersRequest,
  setInvitationAccessDeniedModalState,
  setIsOpenRequestUpgradeModal,
  setIsOpenPurchaseSeatsModal,
  setIsOpenAddSeatsCheckout,
  setIsGetEnterpriseModalOpen,
  setIsOpenUnableJoinTeamModal,
  teamsState,
} from './slices/teamsSlice';
import conversationsListReducer, {
  setPageToken,
  setTaskStatusFilter,
  setTaskSkillFilter,
  toggleConversationListCollapse,
  conversationsListState,
} from './slices/conversationsListSlice';
import compareModeReducer, {
  setFirstCompareModel,
  setSecondCompareModel,
  compareModeState,
} from './slices/compareModeSlice';
import contextLimitModalReducer, {
  setShouldShowContextLimitModal,
  contextLimitModalState,
} from './slices/contextLimitModalSlice';

import migrationToSNModalReducer, {
  setShouldShowMigrationToSNModal,
  migrationToSNModalState,
} from './slices/migrationToSNModalStateSlice';

import { apiServices } from './services/index';

const createNoopStorage = () => {
  return {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    getItem(_key: string) {
      return Promise.resolve(null);
    },
    setItem(_key: string, value: string) {
      return Promise.resolve(value);
    },
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    removeItem(_key: string) {
      return Promise.resolve();
    },
  };
};

const storage =
  typeof window !== 'undefined'
    ? createWebStorage('local')
    : createNoopStorage();

const persistConfig = {
  key: 'ninjaRoot',
  storage,
  timeout: 2000, //Set the timeout function to 2 seconds for the splash screen
  stateReconciler: autoMergeLevel2,
  whitelist: [
    'session',
    'threadPersist',
    'activeModelPersist',
    'skillCommandPersist',
    'compareModePersist',
    'choosePlanModalPersist',
  ],
};

const apiReducers = apiServices.reduce(
  (acc, api) => {
    acc[api.reducerPath] = api.reducer;
    return acc;
  },
  {} as Record<string, (typeof apiServices)[number]['reducer']>,
);

const persistedReducer = persistCombineReducers(persistConfig, {
  logout: logoutReducer,
  session: sessionReducer,
  settings: settingsReducer,
  avatar: avatarReducer,
  interfaceControl: interfaceControlReducer,
  modalsControl: modalsControlReducer,
  thread: threadReducer,
  threadPersist: threadPersistReducer,
  abTesting: abTestingReducer,
  imageMasking: imageMaskingReducer,
  teams: teamsSliceReducer,
  conversationsList: conversationsListReducer,
  activeModelPersist: activeModelReducer,
  activeInputBoxModel: activeInputBoxModelReducer,
  skillCommandPersist: skillCommandReducer,
  paymentPageRightSidePanel: paymentPageRightSidePanelReducer,
  manageCreditPageRightSidePanel: manageCreditPageRightSidePanelReducer,
  editor: editorReducer,
  relatedQuestions: relatedQuestionsReducer,
  compareModePersist: compareModeReducer,
  choosePlanModalPersist: choosePlanModalSliceReducer,
  productNotificationModal: productNotificationModalReducer,
  paymentControl: paymentControlReducer,
  contextLimitModal: contextLimitModalReducer,
  migrationToSNModal: migrationToSNModalReducer,
  ...apiReducers,
});

const rootReducer: Reducer = (
  state: ReturnType<typeof persistedReducer>,
  action: AnyAction,
) => {
  if (action.type === 'logout/resetStore') {
    return persistedReducer({ _persist: state._persist }, action);
  }
  return persistedReducer(state, action);
};

const store = configureStore({
  reducer: rootReducer,
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      immutableCheck: false,
      serializableCheck: false,
    }).concat(...apiServices.map((api) => api.middleware)),
});

export const setupStore = (preloadedState?: PreloadedState<RootState>) => {
  return configureStore({
    reducer: rootReducer,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        serializableCheck: {
          ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
        },
      }).concat(...apiServices.map((api) => api.middleware)),
    preloadedState,
  });
};

export type RootState = ReturnType<typeof store.getState>;
export type AppStore = ReturnType<typeof setupStore>;
export type AppDispatch = typeof store.dispatch;

export const useAppDispatch: () => AppDispatch = useDispatch;

export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;

export { resetStore };
export {
  resetSession,
  setNotification,
  clearNotification,
  setAppUser,
  setIsDarkTheme,
  setSocketConnected,
  setOnboardingHintStep,
  setCurrentTaskId,
  showAccessRequestOnSchedulerTask,
  setIsAccessModalShown,
  setFirstUserQuery,
  setPartialAppUser,
  setActiveSuperAgentModel,
  setBasicTierWelcomeLastShown,
  sessionState,
  sessionColorThemeState,
  setIsVirtuosoOn,
};
export { setChatMode, setAvatarQueue, clearAvatarQueue, avatarState };
export { resetSettings, setActiveSettingsTab, settingsState };
export {
  setThreadStatus,
  setMessageTabPersistRegistry,
  threadState,
  threadStatusRegistryState,
  messageTabPersistRegistryState,
};
export {
  setChatModeSidePanel,
  setIsSettingsPanelExpanded,
  setIsRightPanelExpanded,
  setIsLeftPanelExpanded,
  setLeftPanelSections,
  setRightPanelSections,
  setIsRightPanelFullWidth,
  setIsShowAppSwitcherTooltip,
  setIsShowRequestUsageTable,
  interfaceControlState,
};
export {
  toggleWelcomeModal,
  modalsControlState,
  setWelcomeUpsellModalType,
  toggleQuickTourModal,
};
export {
  setSelectedImageMask,
  setCurrentImagesList,
  setSelectedImageIndex,
  setImageMaskingSelectionsCount,
  toggleIsOpenModalImagesList,
  setImageMaskingRightPanelData,
  toggleVisibleBrushSizePanel,
  setImageMaskingIsLoading,
  setMessageId,
  setIsAttachedImageTooltipVisible,
  setIsAttachedImageEditingMode,
  triggerCanvasReset,
  setEditingMode,
  imageMaskingState,
};

export {
  setIsOpenInviteUsersModal,
  setIsOpenJoinWorkspaceModal,
  setSelectedNumberOfSeatsToBuy,
  setIsFirstInviteUsersRequest,
  setInvitationAccessDeniedModalState,
  setIsOpenRequestUpgradeModal,
  setIsOpenPurchaseSeatsModal,
  setIsOpenAddSeatsCheckout,
  setIsGetEnterpriseModalOpen,
  setIsOpenUnableJoinTeamModal,
  teamsState,
};

export {
  setPageToken,
  setTaskStatusFilter,
  setTaskSkillFilter,
  toggleConversationListCollapse,
  conversationsListState,
};
export {
  setEditorType,
  setCodeEditorData,
  setIsWordWrapEnabled,
  setEditorIsDarkTheme,
  setEditorStreaming,
  setAutoSavingStatus,
  editorState,
};

export { setFirstCompareModel, setSecondCompareModel, compareModeState };

export { setActiveModel, activeModelState };
export { setActiveInputBoxModel, activeInputBoxModelState };

export {
  setShouldShowChoosePlanModal,
  setLastTimeChoosePlanModalWasShown,
  setNeverShowChoosePlanModalAgain,
  choosePlanModalState,
};

export {
  setShouldShowNotificationModal,
  setLastTimeNotificationModalWasShown,
  productNotificationModalState,
};

export { setActiveSkillCommand, skillCommandState };

export { setActivePanelView, paymentPageRightSidePanelState };

export { setActiveManageCreditsPanelView, manageCreditPageRightSidePanelState };

export {
  setSelectedPeriod,
  setSelectedFamily,
  setSelectedPlanByFamily,
  setComplexPaymentPlan,
  resetPaymentControl,
  paymentControlState,
};

export { toggleRelatesQuestionsVisible, relatedQuestionsState };

export { setShouldShowContextLimitModal, contextLimitModalState };

export { setShouldShowMigrationToSNModal, migrationToSNModalState };

export const persistor = persistStore(store);

export default store;
